/**
 * An instant on screen: `<time datetime title>`, in the region's format.
 *
 * The only way a screen shows an instant as an element, so one moment reads the
 * same in every table and feed and the exact value is always on hover. A
 * relative phrase decays, so it carries the absolute date and zone in `title`.
 *
 * @module components/ui/Time
 */

import React from "react";

import {
  formatDate,
  formatDateTime,
  formatRelative,
  type InstantInput,
} from "@/utils/format-instant";

/** `date` for a day, `datetime` for the moment, `relative` for feeds. */
export type TimeVariant = "date" | "datetime" | "relative";

export interface TimeProps {
  value: InstantInput;
  variant?: TimeVariant;
  /** Adds the zone abbreviation (`IST`) to the text, for audit detail. */
  zone?: boolean;
  className?: string;
  "data-testid"?: string;
}

function machineValue(value: InstantInput): string | undefined {
  const date =
    value instanceof Date
      ? value
      : new Date(typeof value === "object" ? value.epochMilliseconds : value);
  return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
}

/**
 * @example
 * ```tsx
 * <Time value={event.timestamp} variant="datetime" />
 * ```
 */
export function Time({
  value,
  variant = "datetime",
  zone = false,
  className = undefined,
  "data-testid": testId,
}: TimeProps): React.ReactElement {
  let text: string;
  if (variant === "date") {
    text = formatDate(value);
  } else if (variant === "relative") {
    text = formatRelative(value);
  } else {
    text = formatDateTime(value, { zone });
  }
  const iso = machineValue(value);
  return (
    <time
      dateTime={iso}
      title={iso === undefined ? undefined : formatDateTime(value, { zone: true })}
      className={className}
      data-testid={testId}
    >
      {text}
    </time>
  );
}
